import React, { useMemo, useState } from "react";
import { Boxes, Search, Package, Users } from "lucide-react";

/**
 * StockPickerModal — Fiyat Hesapla ekranındaki "Stoktan Seç" butonundan açılır.
 * Cari kolonu: Stokun `contact_ids` alanı ile bağlanmış cari(ler)i gösterir
 * (üretimin/satışın yapıldığı müşteri; örn. "MOBU METAL"). Tedarikçi değil.
 *
 * Props:
 *   stocks: StockItem[]
 *   contacts?: Contact[]  — id → name eşlemesi ve filtre dropdown'ı için
 *   onPick(stock): void
 *   onClose(): void
 */
export default function StockPickerModal({ stocks, contacts = [], onPick, onClose }) {
  const [q, setQ] = useState("");
  const [contactFilter, setContactFilter] = useState("");

  const norm = (s) => String(s || "").trim().toLocaleLowerCase("tr");
  const fmt = (n, d = 0) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: d, maximumFractionDigits: d });

  // id → contact map
  const contactMap = useMemo(() => {
    const m = new Map();
    for (const c of contacts) m.set(c.id, c);
    return m;
  }, [contacts]);

  // Stok başına bağlı cariler (contact_ids)
  const stockContacts = useMemo(() => {
    const m = new Map();
    for (const s of stocks) {
      const ids = Array.isArray(s.contact_ids) ? s.contact_ids : [];
      const list = ids.map((id) => contactMap.get(id)).filter(Boolean);
      m.set(s.id, list);
    }
    return m;
  }, [stocks, contactMap]);

  // Filtre için eşsiz cari listesi (kaç stok bu carie bağlı)
  const contactList = useMemo(() => {
    const counts = {};
    for (const s of stocks) {
      for (const c of (stockContacts.get(s.id) || [])) {
        counts[c.id] = counts[c.id] || { name: c.name, count: 0 };
        counts[c.id].count += 1;
      }
    }
    return Object.entries(counts)
      .map(([id, v]) => ({ id, name: v.name, count: v.count }))
      .sort((a, b) => a.name.localeCompare(b.name, "tr"));
  }, [stocks, stockContacts]);

  const filtered = useMemo(() => {
    const term = norm(q);
    let out = stocks;
    if (contactFilter) {
      out = out.filter((s) => (s.contact_ids || []).includes(contactFilter));
    }
    if (term) {
      out = out.filter((it) =>
        norm(it.name).includes(term) ||
        norm(it.sku).includes(term) ||
        norm(it.description).includes(term)
      );
    }
    return out;
  }, [stocks, q, contactFilter]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="stock-picker-backdrop">
      <div onClick={(e) => e.stopPropagation()} data-testid="stock-picker-modal"
           className="bg-white border-2 border-black w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Boxes size={16} /> Stoktan Seç ({filtered.length}{filtered.length !== stocks.length ? ` / ${stocks.length}` : ""})
          </h2>
          <button onClick={onClose} className="p-1 text-xl leading-none">×</button>
        </div>

        <div className="p-4 border-b border-black/10 grid grid-cols-1 md:grid-cols-[1fr_260px] gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              autoFocus
              data-testid="stock-picker-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="SKU, ad veya açıklama…"
              className="w-full border border-black/20 pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black"
            />
          </div>
          <div className="relative">
            <Users size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <select
              data-testid="stock-picker-contact"
              value={contactFilter}
              onChange={(e) => setContactFilter(e.target.value)}
              className="w-full border border-black/20 pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black bg-white appearance-none"
              title="Cariye göre filtrele"
            >
              <option value="">Tüm cariler ({stocks.length})</option>
              {contactList.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.count})</option>
              ))}
            </select>
          </div>
        </div>

        {contactFilter && (
          <div className="px-4 py-1.5 border-b border-black/10 bg-sky-50 text-[11px] text-sky-900 font-mono-num flex items-center justify-between">
            <span>
              <b>{contactMap.get(contactFilter)?.name || "?"}</b> cariisine bağlı stoklar gösteriliyor.
            </span>
            <button
              type="button"
              data-testid="stock-picker-contact-clear"
              onClick={() => setContactFilter("")}
              className="underline hover:text-black"
            >
              filtreyi temizle
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-neutral-500 text-sm">
              {stocks.length === 0
                ? "Henüz stok yok. Stoklar sayfasından ekleyin."
                : contactFilter
                  ? `${contactMap.get(contactFilter)?.name || "Bu cari"}'ye bağlı stok bulunamadı.`
                  : "Arama sonucu yok."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-neutral-50">
                <tr className="border-b border-black/10">
                  <th className="text-left px-3 py-2 label-caps">SKU</th>
                  <th className="text-left px-3 py-2 label-caps">Ad</th>
                  <th className="text-right px-3 py-2 label-caps">Ölçü</th>
                  <th className="text-left px-3 py-2 label-caps">Karton</th>
                  <th className="text-left px-3 py-2 label-caps">Cari</th>
                  <th className="text-right px-3 py-2 label-caps">Adet</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const bound = stockContacts.get(s.id) || [];
                  return (
                    <tr key={s.id} onClick={() => onPick(s)}
                        data-testid={`stock-picker-row-${s.id}`}
                        className="border-b border-black/5 hover:bg-emerald-50 cursor-pointer">
                      <td className="px-3 py-2 font-mono-num text-xs font-semibold align-top">{s.sku}</td>
                      <td className="px-3 py-2 align-top">
                        <div className="font-medium flex items-center gap-1.5">
                          <Package size={12} className="text-neutral-400" /> {s.name}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono-num text-xs align-top">
                        {s.length_mm}×{s.width_mm}×{s.height_mm}
                      </td>
                      <td className="px-3 py-2 text-xs align-top">
                        {(s.flute || s.quality) ? (
                          <>
                            <span className="font-semibold">{s.flute || "—"}</span>
                            {s.quality && <> · {s.quality}</>}
                            {s.grammage != null && Number(s.grammage) >= 1 && <span className="text-neutral-500"> · {fmt(s.grammage, 0)} gr</span>}
                          </>
                        ) : <span className="text-neutral-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-[11px] align-top">
                        {bound.length === 0 ? (
                          <span className="text-neutral-300">—</span>
                        ) : bound.length <= 2 ? (
                          <div className="flex flex-wrap gap-1">
                            {bound.map((c) => (
                              <span key={c.id} className="inline-block px-1.5 py-0.5 border border-black/15 bg-neutral-50">{c.name}</span>
                            ))}
                          </div>
                        ) : (
                          <span title={bound.map((c) => c.name).join(", ")}>
                            {bound[0].name} <span className="text-neutral-400">+{bound.length - 1}</span>
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-num text-xs align-top">
                        {fmt(s.qty_on_hand, 0)} <span className="text-neutral-400">{s.unit}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="p-3 border-t border-black/10 bg-neutral-50 text-[11px] text-neutral-500">
          İpucu: Stok kartına <b>Cari(ler)</b> alanından müşteri bağlayarak burada listelenmesini sağlayabilirsiniz.
        </div>
      </div>
    </div>
  );
}
